import { z } from "zod";

import { getConditionFieldIssue, getNodeContract, getNodeDataflowIssues } from "./node-contracts";
import {
  ACTION_CONFIG_REQUIREMENTS,
  isPublishableNodeType,
  isValidNodeType,
} from "./workflow-node-types";
import type { ActionType } from "./workflow-node-types";
import { canonicalConditionOperator, isPresenceOperator } from "./event-conditions";
import {
  WORKFLOW_TRIGGER_CATALOG,
  type EventBackedTriggerType,
  type WorkflowTriggerConditionField,
} from "./workflow-trigger-catalog";
import {
  CONDITION_NODE_OPERATORS,
  isConditionNodeOperator,
  isTagPredicateOperator,
  readScheduleTrigger,
  readTagAppliedTrigger,
  readTriggerSource,
  TAG_TRIGGER_EVENTS,
} from "./workflow-graph";
import { isTagPublicId } from "./tags";
import { zOpaqueId } from "./zod-format";
import type { WorkflowGraph } from "./workflow-graph";
export type { WorkflowGraph };

function graphKey(maxLength: number) {
  return zOpaqueId("workflow_graph_key").refine((id) => id.length <= maxLength, {
    message: `This id is longer than ${maxLength} characters. Shorten it.`,
  });
}

/** One stored condition clause, as a workflow trigger and an agent listener both keep it. */
export const triggerConditionClauseSchema = z.object({
  id: graphKey(64),
  field: z.string().min(1).max(128),
  operator: z.string().min(1).max(64),
  value: z.unknown(),
  connector: z.enum(["and", "or"]),
});

export const workflowNodeSchema = z
  .object({
    id: graphKey(128),
    kind: z.enum(["trigger", "condition", "action"]),
    type: z.string().min(1).max(128),
    name: z.string().min(1).max(255),
    config: z.record(z.string(), z.unknown()),
    position: z.object({ x: z.number(), y: z.number() }),
    conditions: z.array(triggerConditionClauseSchema).optional(),
  })
  .passthrough();

export const workflowEdgeSchema = z.object({
  id: graphKey(256),
  source: z.string().min(1).max(128),
  target: z.string().min(1).max(128),
});

export const workflowGraphSchema = z.object({
  nodes: z.array(workflowNodeSchema).min(1).max(500),
  edges: z.array(workflowEdgeSchema).max(1000),
});

export type WorkflowGraphInput = z.infer<typeof workflowGraphSchema>;

export type WorkflowValidationFailure = {
  ok: false;
  error: string;
  details?: Record<string, string>;
};

export type WorkflowValidationSuccess = { ok: true };
export type WorkflowValidationResult = WorkflowValidationSuccess | WorkflowValidationFailure;

export function validateGraphInvariants(graph: WorkflowGraphInput): WorkflowValidationResult {
  const triggers = graph.nodes.filter(
    (node: WorkflowGraphInput["nodes"][number]) => node.kind === "trigger",
  );
  if (triggers.length !== 1) return { ok: false, error: "GRAPH_REQUIRES_ONE_TRIGGER" };

  const nodeIds = new Set(graph.nodes.map((node: WorkflowGraphInput["nodes"][number]) => node.id));
  for (const edge of graph.edges) {
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
      return { ok: false, error: "GRAPH_EDGE_REFERENCES_UNKNOWN_NODE" };
    }
  }

  const triggerId = triggers[0]!.id;
  if (graph.edges.some((edge: WorkflowGraphInput["edges"][number]) => edge.target === triggerId)) {
    return { ok: false, error: "GRAPH_TRIGGER_HAS_INCOMING_EDGE" };
  }

  const indegree = new Map<string, number>();
  for (const node of graph.nodes) indegree.set(node.id, 0);
  for (const edge of graph.edges) indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
  const queue: string[] = [];
  for (const [id, deg] of indegree) if (deg === 0) queue.push(id);

  let visited = 0;
  while (queue.length > 0) {
    const id = queue.shift()!;
    visited++;
    for (const edge of graph.edges) {
      if (edge.source !== id) continue;
      const next = (indegree.get(edge.target) ?? 0) - 1;
      indegree.set(edge.target, next);
      if (next === 0) queue.push(edge.target);
    }
  }

  if (visited !== graph.nodes.length) return { ok: false, error: "GRAPH_CONTAINS_CYCLE" };
  return { ok: true };
}

/**
 * A tag branch tests a resource id against a tag id; a text field or value left
 * behind by an operator switch evaluates to a silent `false` at run time.
 */
function tagPredicateIssue(config: Record<string, unknown>): string | null {
  if (!isTagPredicateOperator(config.operator)) return null;
  const field = config.field;
  if (typeof field !== "string" || field.trim().length === 0) {
    return "needs the resource it tests — name where this run carries the id, such as input.resourceId";
  }
  if (!isTagPublicId(config.value)) {
    return "needs a tag — open the step and choose the tag this branch tests";
  }
  return null;
}

/** A branch with no field, no operator or one the runner does not know is always false, so it cannot be published. */
function branchPredicateIssue(config: Record<string, unknown>): string | null {
  const operator = canonicalConditionOperator(config.operator);
  if (operator === null || operator === "") {
    return "needs an operator — open the step and choose how the field is compared";
  }
  if (!isConditionNodeOperator(operator)) {
    return `uses the operator "${operator}", which no branch can evaluate — open the step and choose one of: ${CONDITION_NODE_OPERATORS.join(", ")}`;
  }
  if (typeof config.field !== "string" || config.field.trim().length === 0) {
    return "needs a field — open the step and choose what this branch compares";
  }
  return null;
}

function isPresentConfigValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function triggerConditionsIssue(
  node: WorkflowGraphInput["nodes"][number],
  phase: "draft" | "publish",
): string | null {
  if (node.kind !== "trigger" || !(node.type in WORKFLOW_TRIGGER_CATALOG)) return null;
  const definition = WORKFLOW_TRIGGER_CATALOG[node.type as EventBackedTriggerType];
  const fields: Readonly<Record<string, WorkflowTriggerConditionField>> =
    definition.conditionFields;
  // Custom triggers without condition fields ignore historical clauses their editor cannot show.
  if (Object.keys(fields).length === 0) return null;

  for (const clause of node.conditions ?? []) {
    const field = Object.hasOwn(fields, clause.field) ? fields[clause.field] : undefined;
    if (!field) {
      return `"${definition.label}" cannot filter on "${clause.field}". Open the trigger step and choose a supported field.`;
    }
    const operator = canonicalConditionOperator(clause.operator);
    if (!operator || !(field.operators as readonly string[]).includes(operator)) {
      return `"${definition.label}" filter "${field.label}" does not support operator "${clause.operator}". Open the trigger step and choose a supported operator.`;
    }
    if (isPresenceOperator(operator)) continue;
    if (field.input.kind === "select") {
      if (phase === "draft" && clause.value === "") continue;
      const values = new Set(field.input.options.map((option) => option.value));
      if (typeof clause.value !== "string" || !values.has(clause.value)) {
        return `"${definition.label}" filter "${field.label}" requires one of: ${field.input.options.map((option) => option.label).join(", ")}. Open the trigger step and choose a ${field.label.toLowerCase()}.`;
      }
    }
  }
  return null;
}

/**
 * Validate an action node's config. Prefers the typed Zod schema from the
 * contract registry when the node type has one registered; otherwise falls
 * back to the legacy `requiredFields` list. Returns the first failure
 * message, or `null` when the config is valid.
 *
 * When `dataflow` is supplied, also rejects template tags that point at
 * unknown nodes, downstream nodes, or paths that don't exist in the
 * upstream node's registered output schema.
 */
function validateActionConfig(
  type: string,
  config: Record<string, unknown>,
  dataflow?: { graph: WorkflowGraph; nodeId: string },
): string | null {
  const contract = getNodeContract(type);
  if (contract) {
    const result = contract.configSchema.safeParse(config);
    if (!result.success) {
      const issue = result.error.issues[0];
      if (!issue) return `Action "${type}" has invalid config`;
      const path = issue.path.length > 0 ? `config.${issue.path.join(".")}` : "config";
      return `Action "${type}" ${path}: ${issue.message}`;
    }
  } else {
    const requirement = ACTION_CONFIG_REQUIREMENTS[type as ActionType];
    if (requirement) {
      for (const field of requirement.requiredFields) {
        if (!isPresentConfigValue(config[field])) {
          return `Action "${type}" requires config.${field}`;
        }
      }
    }
  }

  if (dataflow) {
    const dataflowIssues = getNodeDataflowIssues(dataflow.graph, dataflow.nodeId);
    // Only errors gate publish; advisory warnings (e.g. nullable-binding
    // nudges) are surfaced inline in the editor but mustn't block.
    const firstError = dataflowIssues.find((issue) => issue.severity === "error");
    if (firstError) {
      const path = firstError.path.length > 0 ? `config.${firstError.path.join(".")}` : "config";
      return `Action "${type}" ${path}: ${firstError.message}`;
    }
  }

  return null;
}

/** A single config-level issue, keyed by its field path. */
export type NodeConfigIssue = {
  /** Dotted path: `[]` for root-level refinements, otherwise field path. */
  path: string[];
  /** Human-readable message — Zod's message for contract-driven nodes. */
  message: string;
  /**
   * `error` — runtime is guaranteed (or near-guaranteed) to fail; Publish
   * is gated. `warning` — runtime *may* fail (soft advisory like a
   * nullable-binding nudge). The editor surfaces both inline so authors
   * see them, but only errors block Publish.
   */
  severity: "error" | "warning";
};

/**
 * Return all config issues for an action node, keyed by Zod issue path.
 * Used by the editor's config panels to render inline per-field errors.
 *
 * Issues from the contract registry's Zod schema are returned verbatim.
 * For nodes that fall back to the legacy `requiredFields` list, every
 * missing required field becomes a synthetic `Required field` issue.
 *
 * When `graph` is supplied, dataflow issues from {@link getNodeDataflowIssues}
 * are appended — these catch `{{ }}` tags that point at unknown nodes,
 * downstream nodes, or paths that don't exist in the upstream output.
 */
export function getNodeConfigIssues(
  type: string,
  config: Record<string, unknown>,
  dataflow?: { graph: WorkflowGraph; nodeId: string },
): NodeConfigIssue[] {
  const out: NodeConfigIssue[] = [];

  const contract = getNodeContract(type);
  if (contract) {
    const result = contract.configSchema.safeParse(config);
    if (!result.success) {
      for (const issue of result.error.issues) {
        out.push({ path: issue.path.map(String), message: issue.message, severity: "error" });
      }
    }
  } else {
    const requirement = ACTION_CONFIG_REQUIREMENTS[type as ActionType];
    if (requirement) {
      for (const field of requirement.requiredFields) {
        if (!isPresentConfigValue(config[field])) {
          out.push({ path: [field], message: "Required field", severity: "error" });
        }
      }
    }
  }

  if (dataflow) {
    for (const issue of getNodeDataflowIssues(dataflow.graph, dataflow.nodeId)) {
      out.push({ path: issue.path, message: issue.message, severity: issue.severity });
    }
  }

  return out;
}

/**
 * Count how many distinct config issues an action node has. Used by the
 * editor to show a "Configure N required fields" indicator inline on a
 * node card without opening its config panel.
 *
 * Uses the contract registry's Zod schema when present (counts unique
 * issue paths), falling back to the legacy required-fields list. When
 * `graph` is supplied, also includes dataflow issues from upstream
 * template-tag references.
 *
 * `severity` filter: pass `"error"` to count only Publish-blocking
 * issues; omit it to count both errors and warnings.
 *
 * Prefer {@link getNodeIssueCounts} when the caller needs both an error
 * count AND a warning count — it walks the issue list once instead of
 * the two times this helper would.
 */
export function getNodeConfigIssueCount(
  type: string,
  config: Record<string, unknown>,
  dataflow?: { graph: WorkflowGraph; nodeId: string },
  severity?: NodeConfigIssue["severity"],
): number {
  // Collapse issues by path so two issues on the same field (e.g. type +
  // min length) don't double-count.
  const paths = new Set<string>();
  for (const issue of getNodeConfigIssues(type, config, dataflow)) {
    if (severity && issue.severity !== severity) continue;
    paths.add(issue.path.join("."));
  }
  return paths.size;
}

/**
 * Errors-and-warnings count for an action node in one pass. Walks the
 * issue list once and partitions by severity, deduping issue paths
 * within each bucket. The editor calls this per-node — `getNodeConfigIssues`
 * runs the BFS ancestor walk + Zod schema validation, so doing both
 * counts in one call halves the work compared to two separate
 * `getNodeConfigIssueCount` calls.
 */
export function getNodeIssueCounts(
  type: string,
  config: Record<string, unknown>,
  dataflow?: { graph: WorkflowGraph; nodeId: string },
): { errors: number; warnings: number } {
  const errorPaths = new Set<string>();
  const warningPaths = new Set<string>();
  for (const issue of getNodeConfigIssues(type, config, dataflow)) {
    const key = issue.path.join(".");
    if (issue.severity === "error") errorPaths.add(key);
    else warningPaths.add(key);
  }
  return { errors: errorPaths.size, warnings: warningPaths.size };
}

export function validateNodeTypes(graph: WorkflowGraphInput): WorkflowValidationResult {
  const details: Record<string, string> = {};

  for (const node of graph.nodes) {
    if (!isValidNodeType(node.kind, node.type)) {
      details[node.id] = `Unknown ${node.kind} type "${node.type}"`;
      continue;
    }

    const conditionIssue = triggerConditionsIssue(node, "draft");
    if (conditionIssue) {
      details[node.id] = conditionIssue;
      continue;
    }

    // An email trigger may carry a typed `config.source`. It is optional
    // (source-less = match any address), but when present it must be a
    // well-formed TriggerSource. `readTriggerSource` returns null for a
    // present-but-malformed source, which we reject here.
    if (node.kind === "trigger" && node.type === "email_received") {
      const config = node.config as Record<string, unknown>;
      if (config.source != null && readTriggerSource({ config }) === null) {
        details[node.id] = `Trigger "${node.type}" has a malformed config.source`;
      }
    }

    // A schedule trigger carries cron + timezone in config. Stay lenient at
    // save time (an unconfigured schedule is a valid in-progress draft, like an
    // email trigger with no source) — only reject a partially-filled but
    // malformed config. The required-at-publish check lives in
    // validatePublishReadiness.
    if (node.kind === "trigger" && node.type === "schedule") {
      const config = node.config as Record<string, unknown>;
      const hasAnyScheduleConfig = config.cron != null || config.timezone != null;
      if (hasAnyScheduleConfig && readScheduleTrigger({ config }) === null) {
        details[node.id] = `Trigger "${node.type}" needs both a cron expression and a timezone`;
      }
    }

    // A present-but-unknown `event` would be silently coerced to "applied" by
    // the reader, firing on a path the author never chose.
    if (node.kind === "trigger" && node.type === "tag_applied") {
      const event = (node.config as Record<string, unknown>).event;
      if (event != null && !(TAG_TRIGGER_EVENTS as readonly unknown[]).includes(event)) {
        details[node.id] =
          `Trigger "${node.type}" config.event must be ${TAG_TRIGGER_EVENTS.join(" or ")}`;
      }
    }

    if (node.kind !== "action") continue;
    const message = validateActionConfig(node.type, node.config, {
      graph: graph as unknown as WorkflowGraph,
      nodeId: node.id,
    });
    if (message) {
      details[node.id] = message;
    }
  }

  return Object.keys(details).length > 0
    ? { ok: false, error: "GRAPH_INVALID_NODE_CONFIG", details }
    : { ok: true };
}

export function validatePublishReadiness(
  graph: WorkflowGraph | WorkflowGraphInput,
): WorkflowValidationResult {
  const details: Record<string, string> = {};
  let hasNonPublishableNode = false;
  let hasIncompleteTrigger = false;
  let hasInvalidConfig = false;

  for (const node of graph.nodes) {
    if (!isPublishableNodeType(node.kind, node.type)) {
      details[node.id] = `Node "${node.type}" is not allowed in a published workflow`;
      hasNonPublishableNode = true;
      continue;
    }

    const conditionIssue = triggerConditionsIssue(node, "publish");
    if (conditionIssue) {
      details[node.id] = conditionIssue;
      hasIncompleteTrigger = true;
      continue;
    }

    // A schedule trigger can't fire without a cadence — require a well-formed
    // cron + timezone before publish (the semantic cron check happens in the
    // API publish handler, which has the cron parser).
    if (node.kind === "trigger" && node.type === "schedule") {
      if (readScheduleTrigger({ config: node.config as Record<string, unknown> }) === null) {
        details[node.id] = `Trigger "${node.type}" requires a cron expression and timezone`;
        hasIncompleteTrigger = true;
      }
      continue;
    }

    // Empty `tagIds` / `resourceTypes` are "any" to the matcher — a
    // workspace-wide scope the editor never offers, so it can't be published.
    if (node.kind === "trigger" && node.type === "tag_applied") {
      const tagConfig = readTagAppliedTrigger({ config: node.config as Record<string, unknown> });
      if (tagConfig.tagIds.length === 0 || tagConfig.resourceTypes.length === 0) {
        details[node.id] =
          `Trigger "${node.type}" requires at least one tag and one resource type — open the trigger step and choose which tags this workflow watches`;
        hasIncompleteTrigger = true;
      }
      continue;
    }

    if (node.kind === "condition") {
      // Only a real branch carries a predicate; the placeholder and else nodes are drawn, not evaluated.
      const config = node.config as Record<string, unknown>;
      const issue =
        tagPredicateIssue(config) ??
        (node.type === "field_equals" ? branchPredicateIssue(config) : null);
      if (issue) {
        details[node.id] = `Condition "${node.name}" ${issue}`;
        hasInvalidConfig = true;
        continue;
      }
      const fieldIssue =
        node.type === "field_equals"
          ? getConditionFieldIssue(graph as WorkflowGraph, node.id)
          : null;
      if (fieldIssue?.severity === "error") {
        // Worded for the editor and for API callers alike: both set the same "field".
        const resolution = fieldIssue.suggestion
          ? `change the field to \`${fieldIssue.suggestion}\``
          : isTagPredicateOperator(config.operator)
            ? "change the field to where this run carries the resource id, such as `input.resourceId`"
            : "change the field to an output of an earlier step or the trigger";
        details[node.id] = `Condition "${node.name}": field ${fieldIssue.message} — ${resolution}`;
        hasInvalidConfig = true;
      }
      continue;
    }

    if (node.kind !== "action") continue;

    const requirement = ACTION_CONFIG_REQUIREMENTS[node.type as ActionType];
    if (!requirement.implemented) {
      details[node.id] =
        node.type === "run_classifier"
          ? `Classifier step "${node.name}" cannot run in a workflow. Remove this step before publishing and run the classifier separately.`
          : `Action "${node.type}" is not implemented`;
      continue;
    }

    // Same per-node config validation as validateNodeTypes — publishing
    // shouldn't pass when the typed contract rejects the config.
    const message = validateActionConfig(node.type, node.config as Record<string, unknown>, {
      graph: graph as WorkflowGraph,
      nodeId: node.id,
    });
    if (message) {
      details[node.id] = message;
      hasInvalidConfig = true;
    }
  }

  // Pick the headline by the most specific issue: a non-publishable node, then
  // an incomplete schedule config (invalid/incomplete configuration — not
  // "unimplemented actions", which would read as a misleading headline).
  return Object.keys(details).length > 0
    ? {
        ok: false,
        error: hasNonPublishableNode
          ? "GRAPH_NOT_PUBLISH_READY"
          : hasIncompleteTrigger || hasInvalidConfig
            ? "GRAPH_INVALID_NODE_CONFIG"
            : "GRAPH_HAS_UNIMPLEMENTED_ACTIONS",
        details,
      }
    : { ok: true };
}
